// familienstand.ts — EIN gespeicherter Wert, ZWEI Eingabefelder.
//
// Der Familienstand liegt als einzelner Freitext in `Contact.maritalStatus`
// (varchar(100)). Angeboten wird trotzdem eine feste Liste — sonst stünden für
// „ledig“ fünf Schreibweisen in der Datenbank. Alles, was in diese Liste nicht
// passt (getrennt lebend, eheähnliche Gemeinschaft, verpartnert …), läuft über
// „Sonstiges“ plus Freitext.
//
// Warum hier und nicht je Maske: Anlage und Akte zeigen denselben Wert. Solange
// jede für sich entschied, was „Sonstiges“ bedeutet, gab es das Freitextfeld nur
// in der alten, nicht mehr gerouteten SinglePageForm — die Anlage schrieb
// `familyStatusOther` nie, obwohl der Payload-Builder es las, und die Akte bot
// überhaupt keinen Weg, einen Altwert wie „getrennt lebend“ zu ändern.
//
// GRUNDREGEL für den Freitext: gespeichert wird ohne Rand-Leerraum, ANGEZEIGT
// wird er mit. Beide Masken binden das Feld controlled an den abgeleiteten Wert
// — würde hier getrimmt, schriebe React das getrimmte Ergebnis sofort in den
// DOM zurück und es ließe sich kein einziges Leerzeichen tippen („getrennt
// lebend“ würde zu „getrenntlebend“). Genau dieser Fehler ist im Repo schon
// einmal aufgetreten (Verantwortlichkeiten, PO 10.08.2026) und wird deshalb
// hier wie dort behandelt: trimmen erst an der Speicher-Grenze.

/** Feste Auswahl beider Masken; „Sonstiges“ öffnet das Freitextfeld. */
export const FAMILIENSTAND_OPTIONS: string[] = [
  "Ledig",
  "Verheiratet",
  // „Zusammenlebend" war bis dahin nur ein Beispiel im Sonstiges-Freitext und
  // stand deshalb in PROD in mehreren Schreibweisen („eheähnliche
  // Gemeinschaft", „Lebensgemeinschaft", „zusammenlebend"). Als eigene Option
  // ist der Wert auswertbar — und er ist der Fall, den der Haushalt vorschlagen
  // kann, wenn zwei Erwachsene ohne Trauschein zusammen wohnen (PO 13.09.2026).
  "Zusammenlebend",
  "Geschieden",
  "Verwitwet",
  "Sonstiges",
];

/** Der Eintrag, der das Freitextfeld aufklappt. */
export const FAMILIENSTAND_SONSTIGES = "Sonstiges";

/** Was im Freitextfeld als Beispiel steht — die drei Werte der alten Maske. */
export const FAMILIENSTAND_SONSTIGES_PLATZHALTER =
  "z. B. getrennt lebend, eheähnliche Gemeinschaft, verpartnert";

/**
 * Zerlegt den gespeicherten Wert in Auswahl + Freitext.
 *
 * Ein Wert, den die Liste nicht kennt, ist ein „Sonstiges“-Freitext — er bleibt
 * damit sichtbar UND änderbar, statt als stumme Zusatz-Option am Ende der Liste
 * zu hängen.
 *
 * Getrimmt wird nur für den Listen-Abgleich; als `freitext` geht der Wert
 * unverändert zurück, damit ein gerade getipptes Leerzeichen stehen bleibt.
 */
/**
 * Der Listeneintrag zu einem gespeicherten Wert — ohne Rücksicht auf Groß-/
 * Kleinschreibung. Im Bestand steht der Familienstand auch als „verheiratet“
 * oder „ledig“; das ist derselbe Stand wie der Listeneintrag und muss sich
 * auch so verhalten (Hochzeitsbereich, Ableitung). Bis 01.10.2026 hat die
 * Anlage-Ableitung solche Werte stillschweigend auf „Verheiratet“
 * umgeschrieben und damit die Lücke verdeckt (Regression aus #2031).
 */
const listenEintrag = (wert: string): string | undefined => {
  const w = wert.trim().toLowerCase();
  return w ? FAMILIENSTAND_OPTIONS.find((o) => o.toLowerCase() === w) : undefined;
};

export const familienstandZerlegen = (
  gespeichert?: string | null,
): { auswahl: string; freitext: string } => {
  const roh = gespeichert ?? "";
  const wert = roh.trim();
  if (!wert) return { auswahl: "", freitext: "" };
  const eintrag = listenEintrag(wert);
  if (eintrag) return { auswahl: eintrag, freitext: "" };
  return { auswahl: FAMILIENSTAND_SONSTIGES, freitext: roh };
};

/**
 * Setzt Auswahl + Freitext wieder zu dem einen Wert zusammen, der im Formular
 * steht.
 *
 * Bei „Sonstiges“ ohne Freitext bleibt „Sonstiges“ stehen: die Auswahl war eine
 * bewusste Angabe, und nur so überlebt sie das nächste Laden (siehe
 * `familienstandZerlegen`). Ein Freitext aus einer vorherigen Auswahl wird
 * verworfen, sobald wieder ein Listeneintrag gewählt ist — sonst schriebe die
 * Anlage über `familyStatusOther || familyStatus` den alten Freitext zurück.
 *
 * Der Rand-Leerraum eines nicht-leeren Freitextes bleibt erhalten (siehe
 * Grundregel oben); entfernt wird er erst von `familienstandZumSpeichern`.
 */
export const familienstandZusammensetzen = (auswahl: string, freitext: string): string => {
  const gewaehlt = (auswahl ?? "").trim();
  if (!gewaehlt) return "";
  if (gewaehlt !== FAMILIENSTAND_SONSTIGES) return gewaehlt;
  const text = freitext ?? "";
  return text.trim() ? text : FAMILIENSTAND_SONSTIGES;
};

/**
 * Was tatsächlich in `Contact.maritalStatus` landet.
 *
 * Während des Tippens trägt der Freitext absichtlich Rand-Leerraum — hier, an
 * der Speicher-Grenze, fällt er weg. `null`/`undefined` bleiben, wie sie sind:
 * ein `undefined` würde den Schlüssel aus dem PUT-Body werfen, ein `null` löscht
 * den Wert — das darf diese Funktion nicht ineinander umdeuten.
 */
export const familienstandZumSpeichern = <T extends string | null | undefined>(wert: T): T =>
  (typeof wert === "string" ? (wert.trim() as T) : wert);

/**
 * Anlage-Maske: dort stehen Auswahl und Freitext in ZWEI Formularfeldern
 * (`familyStatus`, `familyStatusOther`), aus denen
 * `buildPrivateContactCreatePayload` über `familyStatusOther || familyStatus`
 * den einen gespeicherten Wert bildet.
 *
 * Der Freitext kommt ROH aus seinem eigenen Feld und läuft nicht durch
 * `familienstandZerlegen` — sonst schluckte das controlled input jedes gerade
 * getippte Leerzeichen. Nur der Altwert, der beim Umwandeln noch in
 * `familyStatus` steht („getrennt lebend“), wird von dort geholt.
 */
export const familienstandAusFormular = (
  familyStatus?: string | null,
  familyStatusOther?: string | null,
): { auswahl: string; freitext: string } => {
  const ausAltwert = familienstandZerlegen(familyStatus);
  return {
    auswahl: ausAltwert.auswahl,
    freitext: (familyStatusOther ?? "") || ausAltwert.freitext,
  };
};

/**
 * Neue Auswahl → beide Formularfelder der Anlage.
 *
 * Bei „Sonstiges“ wandert der bisher ANGEZEIGTE Freitext ausdrücklich nach
 * `familyStatusOther`: beim Umwandeln steht er noch in `familyStatus` und ginge
 * verloren, sobald „Sonstiges“ (erneut) gewählt wird — gespeichert würde dann
 * der Sentinel „Sonstiges“ statt „getrennt lebend“. Bei jeder anderen Auswahl
 * muss er weg, sonst gewänne er über die Vorrangregel des Payload-Builders
 * gegen die gerade getroffene Auswahl.
 */
export const familienstandAuswahlInsFormular = (
  auswahl: string,
  freitextBisher: string,
): { familyStatus: string; familyStatusOther: string } => ({
  familyStatus: auswahl,
  familyStatusOther: auswahl === FAMILIENSTAND_SONSTIGES ? freitextBisher : "",
});

/**
 * Neuer Freitext → beide Formularfelder der Anlage. Die Auswahl geht dabei auf
 * den Sentinel, damit das Feld sichtbar bleibt, solange getippt wird.
 */
export const familienstandFreitextInsFormular = (
  freitext: string,
): { familyStatus: string; familyStatusOther: string } => ({
  familyStatus: FAMILIENSTAND_SONSTIGES,
  familyStatusOther: freitext,
});

/**
 * Familienstände, bei denen nach dem Hochzeitstag gefragt wird.
 *
 * „Geschieden" und „Verwitwet" gehören dazu: Auch wer nicht mehr verheiratet
 * IST, war es — und das Datum steht dann in Urkunden, Verträgen und Erbfragen.
 * Es nur bei „Verheiratet" anzubieten hieße, die Angabe beim Wechsel des
 * Standes unsichtbar zu machen, obwohl sie weiter gilt.
 */
const MIT_HOCHZEITSTAG = new Set(["Verheiratet", "Geschieden", "Verwitwet"]);

/** Soll die Maske den Hochzeitstag anbieten? */
export const zeigeHochzeitstag = (familienstand?: string | null): boolean =>
  MIT_HOCHZEITSTAG.has(listenEintrag(String(familienstand ?? "")) ?? "");
